import { useEffect, useRef, useState } from "react";
import { Button } from "@legends/ui";
import { verifyEmail, type AuthUser } from "./api";

export interface VerifyEmailScreenProps {
  /** The one-time token from the emailed link (?token=…). */
  token: string;
  /** Called on success — the fan is now verified AND signed in (the API set the
   *  session cookie); App adopts the authenticated status. */
  onVerified: (user: AuthUser) => void;
  /** Return to the sign-in screen (shown on an invalid/expired link). */
  onBackToLogin: () => void;
}

// Landing page for the verification link (LSC-554 / ADR 0233) at /verify-email?token=…
// (resolved before AuthGate). Confirms the token once on mount; on success the fan is
// signed in and dropped into the app, on failure they get a clear retry path.
export default function VerifyEmailScreen({ token, onVerified, onBackToLogin }: VerifyEmailScreenProps) {
  const [state, setState] = useState<"verifying" | "ok" | "error">("verifying");
  const [message, setMessage] = useState("");
  const [verifiedUser, setVerifiedUser] = useState<AuthUser | null>(null);
  const ran = useRef(false); // consume the one-time token exactly once (StrictMode double-mount)

  // Confirm the token once, keyed ONLY on the token. Do NOT depend on onVerified: App
  // passes a fresh inline closure every render and re-renders this screen mid-flight
  // (its boot fetches resolve → setCave/setStatus). With onVerified in the deps + the
  // ran-guard, each re-run's cleanup flipped the in-flight call's `cancelled` flag while
  // the guard skipped restarting it, so the resolved success was swallowed — "Verifying…"
  // forever though the server had already verified + signed the fan in (LSC-570). Drive
  // success off committed state instead of the async closure.
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setState("error");
      setMessage("This verification link is missing its token.");
      return;
    }
    verifyEmail(token)
      .then((user) => {
        setVerifiedUser(user);
        setState("ok");
      })
      .catch((err: unknown) => {
        setState("error");
        setMessage(err instanceof Error ? err.message : "This verification link is invalid or has expired.");
      });
  }, [token]);

  // Hand the signed-in fan up to App exactly once, driven by the committed success
  // state (not the async closure), so a concurrent App re-render can't drop it. App's
  // onVerified adopts the session + navigates home, which unmounts this screen.
  useEffect(() => {
    if (verifiedUser) onVerified(verifiedUser);
    // onVerified intentionally omitted — verifiedUser transitions once and drives it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifiedUser]);

  return (
    <main
      className="mer-canvas mer-canvas--photo"
      style={{ minHeight: "calc(100vh / var(--ui-zoom, 1))", display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <div
        style={{
          background: "var(--surface-2)",
          border: "1px solid var(--border)",
          padding: "26px 32px 24px",
          width: 360,
          display: "grid",
          gap: 14,
        }}
      >
        {state === "verifying" && (
          <>
            <h1 style={{ margin: 0, fontSize: "var(--fs-h2)" }}>Verifying…</h1>
            <p style={{ margin: 0, fontSize: "var(--fs-sm)", color: "var(--text-dim)" }}>Confirming your email address.</p>
          </>
        )}
        {state === "ok" && (
          <>
            <h1 style={{ margin: 0, fontSize: "var(--fs-h2)" }}>You're in</h1>
            <p style={{ margin: 0, fontSize: "var(--fs-sm)", color: "var(--text-dim)" }}>
              Your email is verified and you're signed in.
            </p>
          </>
        )}
        {state === "error" && (
          <>
            <h1 style={{ margin: 0, fontSize: "var(--fs-h2)" }}>Link expired</h1>
            <p role="alert" style={{ margin: 0, fontSize: "var(--fs-sm)", color: "var(--alert-2)", lineHeight: 1.6 }}>
              {message || "This verification link is invalid or has expired."}
            </p>
            <p style={{ margin: 0, fontSize: "var(--fs-sm)", color: "var(--text-dim)", lineHeight: 1.6 }}>
              Sign in to get a fresh verification email.
            </p>
            <Button variant="accent" full onClick={onBackToLogin}>
              Back to sign in
            </Button>
          </>
        )}
      </div>
    </main>
  );
}
